import { useCallback, useEffect, useRef, useState } from 'react';
/** Long enough to explain a bug, short enough to stay far under the upload cap. */ export const MAX_RECORDING_SECONDS = 120;
// First the browser supports wins. Chrome/Firefox record webm, Safari only mp4;
// the extension is what the transcriber uses to tell them apart.
const FORMATS = [
    {
        mime: 'audio/webm;codecs=opus',
        ext: 'webm'
    },
    {
        mime: 'audio/webm',
        ext: 'webm'
    },
    {
        mime: 'audio/mp4',
        ext: 'mp4'
    },
    {
        mime: 'audio/ogg;codecs=opus',
        ext: 'ogg'
    }
];
/**
 * Records the microphone until stop() or the time cap, then hands the clip to
 * onRecorded. The recorder, not the caller, owns the tracks, so the browser's
 * mic indicator goes off the moment recording ends — including on cancel()
 * and unmount, neither of which produces a clip.
 */ export function useVoiceRecorder(onRecorded) {
    const [isRecording, setIsRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState(null);
    const recorderRef = useRef(null);
    // Bumped by every start() and cancel(). A start() still waiting on the
    // permission prompt compares against it afterwards, so a cancel or unmount
    // in that window releases the mic instead of recording into nothing.
    const sessionRef = useRef(0);
    const onRecordedRef = useRef(onRecorded);
    onRecordedRef.current = onRecorded;
    const stop = useCallback(()=>{
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    }, []);
    /** Abandons the recording: releases the mic and hands no clip to onRecorded. */ const cancel = useCallback(()=>{
        sessionRef.current++;
        const recorder = recorderRef.current;
        recorderRef.current = null;
        if (recorder) {
            recorder.onstop = null;
            if (recorder.state !== 'inactive') recorder.stop();
            recorder.stream.getTracks().forEach((track)=>track.stop());
        }
        setIsRecording(false);
    }, []);
    const start = useCallback(async ()=>{
        setError(null);
        const session = ++sessionRef.current;
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: true
            });
        } catch  {
            if (session === sessionRef.current) {
                setError('Microphone access was blocked. You can still type it instead.');
            }
            return;
        }
        const release = ()=>stream.getTracks().forEach((track)=>track.stop());
        if (session !== sessionRef.current) {
            release();
            return;
        }
        const format = FORMATS.find((f)=>MediaRecorder.isTypeSupported?.(f.mime));
        let recorder;
        try {
            recorder = new MediaRecorder(stream, format ? {
                mimeType: format.mime
            } : undefined);
            const chunks = [];
            recorder.ondataavailable = (e)=>e.data.size > 0 && chunks.push(e.data);
            recorder.onstop = ()=>{
                release();
                recorderRef.current = null;
                setIsRecording(false);
                const type = recorder.mimeType || format?.mime || 'audio/webm';
                onRecordedRef.current({
                    audio: new Blob(chunks, {
                        type
                    }),
                    filename: `report.${format?.ext ?? 'webm'}`
                });
            };
            recorder.start();
        } catch  {
            // An unsupported format throws from the constructor or start(); the
            // mic is already open by then and would otherwise stay lit.
            release();
            setError('Recording could not start in this browser. You can still type it instead.');
            return;
        }
        recorderRef.current = recorder;
        setElapsed(0);
        setIsRecording(true);
    }, []);
    useEffect(()=>{
        if (!isRecording) return;
        const startedAt = Date.now();
        const tick = setInterval(()=>{
            const seconds = Math.floor((Date.now() - startedAt) / 1000);
            setElapsed(seconds);
            if (seconds >= MAX_RECORDING_SECONDS) stop();
        }, 250);
        return ()=>clearInterval(tick);
    }, [
        isRecording,
        stop
    ]);
    // Unmounting mid-sentence must not leave the mic live or hand a clip to a
    // torn-down component.
    useEffect(()=>cancel, [
        cancel
    ]);
    return {
        isRecording,
        elapsed,
        error,
        start,
        stop,
        cancel
    };
}
